import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { publicAPI } from '../api/public';
import type { SenderOrder, SenderDocState } from '../api/public';
import { clearActiveOrder } from '../utils/senderSession';
import {
  CheckCircle2,
  Clock,
  Printer,
  ArrowLeft,
  Phone,
  Plus,
  FileText,
  AlertCircle,
  PackageCheck,
} from 'lucide-react';

const POLL_MS = 5000;
const CLOSED_STATUSES: SenderOrder['order_status'][] = ['completed', 'expired', 'removed'];

// Old links (/track?code=RC000123) track a single document by ID
const legacyDocToOrder = (doc: any): SenderOrder => {
  let state: SenderDocState = doc.status;
  if (doc.status === 'confirmed') state = 'collected';
  if (doc.status === 'deleted') state = doc.confirmed_at ? 'collected' : doc.printed_at ? 'expired' : 'removed';
  const order_status: SenderOrder['order_status'] =
    state === 'uploaded' ? 'waiting' : state === 'printed' ? 'ready' : state === 'collected' ? 'completed' : state;
  return {
    order_status,
    shop_name: doc.shop_name || 'Print Shop',
    shop_public_id: doc.shop_public_id || null,
    shop_phone: doc.shop_phone || null,
    documents: [{
      id: doc.id,
      original_filename: doc.original_filename,
      state,
      print_copies: doc.print_copies || 1,
      uploaded_at: doc.uploaded_at || null,
      printed_at: doc.printed_at || null,
    }],
  };
};

const getSavedSenderName = (): string => {
  try {
    return JSON.parse(localStorage.getItem('lastSenderInfo') || '{}').name || '';
  } catch {
    return '';
  }
};

const STATE_LABEL: Record<SenderDocState, { text: string; className: string }> = {
  uploaded: { text: 'Waiting to print', className: 'text-blue-600' },
  printed: { text: 'Ready for pickup', className: 'text-green-600' },
  collected: { text: 'Collected', className: 'text-slate-500' },
  expired: { text: 'Not collected', className: 'text-amber-600' },
  removed: { text: 'Removed by shop', className: 'text-slate-500' },
};

const SenderTrackingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderToken = searchParams.get('order');
  const legacyCode = searchParams.get('code');
  const shopParam = searchParams.get('shop') || '';

  const [order, setOrder] = useState<SenderOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [finished, setFinished] = useState(false);
  const senderName = getSavedSenderName();

  const pollingActive = useRef(true);
  const isFetching = useRef(false);

  const shopId = order?.shop_public_id || shopParam;
  const shopName = order?.shop_name || searchParams.get('name') || 'the shop';
  const sendMorePath = shopId ? `/shop/${shopId}` : '/send';

  const applyOrder = useCallback((next: SenderOrder) => {
    setOrder(next);
    setError('');
    if (CLOSED_STATUSES.includes(next.order_status)) {
      pollingActive.current = false;
      if (orderToken) clearActiveOrder(next.shop_public_id || shopParam, orderToken);
    }
  }, [orderToken, shopParam]);

  const loadOrder = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    try {
      if (orderToken) {
        applyOrder(await publicAPI.getOrder(orderToken));
      } else if (legacyCode) {
        const documentId = Number(legacyCode.replace(/^RC0*/i, ''));
        if (!documentId) throw Object.assign(new Error('Invalid receipt code'), { status_code: 404 });
        applyOrder(legacyDocToOrder(await publicAPI.getDocumentStatus(documentId)));
      } else {
        setNotFound(true);
      }
    } catch (err: any) {
      if (err?.status_code === 404) {
        pollingActive.current = false;
        setNotFound(true);
        if (orderToken) clearActiveOrder(shopParam, orderToken);
      } else {
        setError('Connection problem. Retrying…');
      }
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  }, [orderToken, legacyCode, shopParam, applyOrder]);

  useEffect(() => {
    pollingActive.current = true;
    loadOrder();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (!pollingActive.current) return;
      if (document.visibilityState === 'visible') await loadOrder();
      if (pollingActive.current) timer = setTimeout(poll, POLL_MS);
    };
    timer = setTimeout(poll, POLL_MS);
    return () => {
      pollingActive.current = false;
      clearTimeout(timer);
    };
  }, [loadOrder]);

  const handleCollected = async () => {
    setConfirming(true);
    try {
      if (orderToken) {
        applyOrder(await publicAPI.confirmOrder(orderToken));
      } else if (order) {
        const printed = order.documents.filter(d => d.state === 'printed');
        await Promise.all(printed.map(d => publicAPI.confirmReceipt(d.id)));
        await loadOrder();
      }
    } catch {
      setError('Could not confirm pickup. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleCallShop = () => {
    if (order?.shop_phone) window.location.href = `tel:${order.shop_phone}`;
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-[2.5rem] p-8 sm:p-10 max-w-md w-full text-center shadow-2xl border border-slate-100"
      >
        {children}
      </motion.div>
    </div>
  );

  const sendMoreButtons = (primaryLabel: string) => (
    <div className="flex flex-col gap-3">
      <button
        onClick={() => navigate(sendMorePath)}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-2xl transition-all font-black uppercase tracking-wider text-xs shadow-lg shadow-blue-500/25 active:scale-95 flex items-center justify-center space-x-2"
      >
        <Plus className="w-4 h-4" />
        <span>{primaryLabel}</span>
      </button>
      <button
        onClick={() => setFinished(true)}
        className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 px-6 rounded-2xl transition-all font-bold text-xs"
      >
        Done
      </button>
    </div>
  );

  if (loading && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Loading your order</p>
        </div>
      </div>
    );
  }

  if (finished) {
    return shell(
      <>
        <div className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center mx-auto mb-6 text-white">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Thank you!</h2>
        <p className="text-slate-500 text-sm mb-6 font-medium">You can close this page now.</p>
        <button
          onClick={() => navigate(sendMorePath)}
          className="text-blue-600 font-black uppercase tracking-wider text-xs hover:underline"
        >
          Send more files to {shopName}
        </button>
      </>
    );
  }

  if (notFound || !order) {
    return shell(
      <>
        <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-500">
          <AlertCircle className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Order not found</h2>
        <p className="text-slate-500 text-sm mb-6 leading-relaxed font-medium">
          This link has expired or is invalid. Files are deleted automatically for your privacy.
        </p>
        {sendMoreButtons('Send Files')}
      </>
    );
  }

  if (order.order_status === 'completed') {
    return shell(
      <>
        <div className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-500/20 text-white">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2 tracking-tight">Order Complete!</h2>
        <p className="text-slate-500 text-sm mb-6 leading-relaxed font-medium">
          You collected your prints from <span className="text-slate-900 font-bold">{shopName}</span>. Your files have been deleted for your privacy.
        </p>
        {sendMoreButtons(`Send more to ${shopName}`)}
      </>
    );
  }

  if (order.order_status === 'expired' || order.order_status === 'removed') {
    const expired = order.order_status === 'expired';
    return shell(
      <>
        <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-amber-600">
          <Clock className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">
          {expired ? 'Order expired' : 'Order closed by shop'}
        </h2>
        <p className="text-slate-500 text-sm mb-6 leading-relaxed font-medium">
          {expired
            ? 'Your prints were not collected within 24 hours, so the files were deleted for your privacy.'
            : `${shopName} removed these files. Contact the shop if this was unexpected.`}
        </p>
        {sendMoreButtons('Send Again')}
      </>
    );
  }

  // Active order: waiting or ready
  const isReady = order.order_status === 'ready';
  const readyCount = order.documents.filter(d => d.state === 'printed').length;
  // Sent is always done; Printing pulses while waiting; everything lights up once ready
  const steps = [
    { icon: CheckCircle2, title: 'Sent', desc: 'Your files reached the shop.', lit: true, pulse: false },
    { icon: Printer, title: 'Printing', desc: 'The shop is printing your files.', lit: isReady, pulse: !isReady },
    { icon: PackageCheck, title: 'Ready for pickup', desc: senderName ? `Tell the shop your name: ${senderName}` : 'Collect your prints at the counter.', lit: isReady, pulse: false },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-32">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 pt-safe-top">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(sendMorePath)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center min-w-0">
            <h1 className="text-sm font-black uppercase tracking-widest text-slate-900 truncate max-w-[180px]">{shopName}</h1>
            <div className="flex items-center justify-center">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Live Tracking</span>
            </div>
          </div>
          <button
            onClick={handleCallShop}
            disabled={!order.shop_phone}
            aria-label="Call shop"
            className={`w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 ${!order.shop_phone ? 'opacity-30' : 'hover:bg-blue-100'} transition-all`}
          >
            <Phone className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 pt-6">
        {error && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold rounded-2xl px-4 py-3">{error}</div>
        )}

        <div className="bg-white rounded-[2rem] p-6 shadow-xl shadow-slate-200/50 border border-slate-100 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">Your order</p>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {isReady ? 'Ready for pickup' : 'Waiting to print'}
              </h2>
            </div>
            <div className="bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
              <span className="text-[10px] font-black text-blue-700 uppercase tracking-widest">
                {order.documents.length} {order.documents.length === 1 ? 'File' : 'Files'}
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-slate-100"></div>
            <div className="space-y-6 relative">
              {steps.map(({ icon: Icon, title, desc, lit, pulse }) => (
                <div key={title} className="flex items-start space-x-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 border-2 transition-colors ${lit ? 'bg-green-600 border-green-600 text-white' : pulse ? 'bg-white border-blue-400 text-blue-500 animate-pulse' : 'bg-white border-slate-200 text-slate-300'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className={`text-sm font-black uppercase tracking-tight ${lit || pulse ? 'text-slate-900' : 'text-slate-400'}`}>{title}</h4>
                    <p className="text-xs text-slate-500 font-medium">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2 mb-3">Files</h3>
        <div className="space-y-3">
          {order.documents.map(doc => (
            <div key={doc.id} className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex items-center space-x-4">
              <div className="w-11 h-11 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-bold text-slate-900 truncate">{doc.original_filename}</h4>
                <span className={`text-[10px] font-black uppercase tracking-widest ${STATE_LABEL[doc.state].className}`}>
                  {STATE_LABEL[doc.state].text}
                  {doc.print_copies > 1 ? ` · ${doc.print_copies} copies` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-blue-50 p-4 rounded-3xl border border-blue-100 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800 font-bold leading-relaxed">
            Keep this page open or come back from the shop's QR code. Files are deleted once you confirm pickup, or 24 hours after printing.
          </p>
        </div>

        <button
          onClick={() => navigate(sendMorePath)}
          className="mt-4 w-full bg-white border border-slate-200 text-slate-700 py-3.5 rounded-2xl font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-2 hover:bg-slate-50"
        >
          <Plus className="w-4 h-4" />
          <span>Add more files to this order</span>
        </button>
      </main>

      {isReady && (
        <div className="fixed bottom-0 inset-x-0 z-[60] bg-white/95 backdrop-blur border-t border-slate-200 p-4">
          <div className="max-w-lg mx-auto">
            <button
              onClick={handleCollected}
              disabled={confirming}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-green-200 active:scale-95 transition-all"
            >
              {confirming ? 'Confirming…' : readyCount > 1 ? `I've collected all ${readyCount} prints` : "I've collected my prints"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SenderTrackingPage;
